/**
 * @fileoverview "Margins per layout" editor for a letterhead variant.
 *
 * A variant shows different zones on different pages (Beacon: header +
 * footer + right band on page 1, header only after that). Each such
 * combination gets its own row here with the nine margin inputs and the
 * ruler diagram. Blank inputs inherit the firm / variant defaults; a row
 * with any value is "Customised". Rows write the variant's full
 * `layoutMargins` list through `onSave`.
 */

import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/misc";
import {
  formatForUnit,
  parseInForUnit,
  type MarginUnit,
} from "@/utils/branding/marginUnits";
import {
  layoutKeysEqual,
  type DocumentPadding,
  type LayoutMarginEntry,
  type PaddingSource,
  type VariantFragments,
} from "@/utils/branding/resolve-letterhead";
import {
  describeLayoutKey,
  PAGE_FOR_CLASS,
  type LayoutCombo,
} from "@/utils/branding/layoutCombos";
import {
  MarginsRulerDiagram,
  type DiagramMarginKey,
  type DiagramValues,
} from "./MarginsRulerDiagram";
import { LetterheadSamplePreview } from "./LetterheadSamplePreview";
import { diagramValuesToPadding } from "@/utils/branding/samplePreview";
import { PAGE_FIT_LOCK_NOTE, zoneLocksForPage } from "@/utils/branding/pageFit";

type FieldKey = DiagramMarginKey;

const ZONES: Array<{
  zone: string;
  fields: Array<{
    key: FieldKey;
    label: string;
    resolved: keyof DocumentPadding;
  }>;
}> = [
  {
    zone: "Header",
    fields: [
      { key: "headerTopPadding", label: "Top", resolved: "headerTop" },
      { key: "headerLeftPadding", label: "Left", resolved: "headerLeft" },
      { key: "headerRightPadding", label: "Right", resolved: "headerRight" },
    ],
  },
  {
    zone: "Body",
    fields: [
      { key: "bodyTopPadding", label: "Top", resolved: "bodyTop" },
      { key: "bodyLeftPadding", label: "Left", resolved: "bodyLeft" },
      { key: "bodyRightPadding", label: "Right", resolved: "bodyRight" },
      { key: "bodyBottomPadding", label: "Bottom", resolved: "bodyBottom" },
    ],
  },
  {
    zone: "Footer",
    fields: [
      { key: "footerBottomPadding", label: "Bottom", resolved: "footerBottom" },
      { key: "footerLeftPadding", label: "Left", resolved: "footerLeft" },
      { key: "footerRightPadding", label: "Right", resolved: "footerRight" },
    ],
  },
];
const ALL_FIELDS = ZONES.flatMap((z) => z.fields);

/** Effective px per field for a row: entry value over the inherited value. */
function effectiveValues(
  entry: PaddingSource | null,
  inherited: DocumentPadding,
): DiagramValues {
  const out: Partial<DiagramValues> = {};
  for (const f of ALL_FIELDS) {
    const v = entry?.[f.key];
    out[f.key] = v ?? inherited[f.resolved];
  }
  return out as DiagramValues;
}

function isCustomised(entry: PaddingSource | null | undefined): boolean {
  return !!entry && ALL_FIELDS.some((f) => entry[f.key] != null);
}

/**
 * The margins a row inherits when it sets nothing: one value for every
 * layout, or a resolver per layout for when the page-fit letterhead on
 * one page class measured a different body column from another's.
 */
export type InheritedPadding =
  DocumentPadding | ((combo: LayoutCombo) => DocumentPadding);

function inheritedFor(
  inherited: InheritedPadding,
  combo: LayoutCombo,
): DocumentPadding {
  return typeof inherited === "function" ? inherited(combo) : inherited;
}

export interface LayoutMarginsEditorProps {
  combos: LayoutCombo[];
  entries: LayoutMarginEntry[];
  /** The margins a row inherits when it sets nothing (firm + variant padding). */
  inherited: InheritedPadding;
  unit: MarginUnit;
  canEdit: boolean;
  onSave: (next: LayoutMarginEntry[]) => Promise<void> | void;
  /**
   * The variant's render fragments. When provided, each row's margins are
   * edited by dragging edges over a live sample-page preview of that
   * layout instead of the abstract ruler diagram.
   */
  fragments?: VariantFragments[];
}

/**
 * One editable sample page per layout combination — the same drag-margin
 * preview the upload editor uses. Dragging an edge writes that layout's
 * margins into the variant's `layoutMargins` (blank rows inherit).
 */
export function LayoutMarginPreviews({
  combos,
  entries,
  inherited,
  unit,
  canEdit,
  onSave,
  fragments,
  width = 420,
}: LayoutMarginsEditorProps & {
  fragments: VariantFragments[];
  width?: number;
}) {
  const writeEntry = async (
    key: LayoutCombo["key"],
    padding: PaddingSource | null,
  ) => {
    const rest = entries.filter((e) => !layoutKeysEqual(e.key, key));
    const next =
      padding && isCustomised(padding) ? [...rest, { key, padding }] : rest;
    try {
      await onSave(next);
    } catch {
      toast.error("Failed to save margins.");
    }
  };
  return (
    <div className="flex flex-wrap items-start justify-center gap-6 lg:flex-col lg:items-center">
      {combos.map((combo) => {
        const entry =
          entries.find((e) => layoutKeysEqual(e.key, combo.key))?.padding ??
          null;
        return (
          <ComboPreview
            key={combo.title}
            combo={combo}
            entry={entry}
            inherited={inheritedFor(inherited, combo)}
            unit={unit}
            canEdit={canEdit}
            fragments={fragments}
            width={width}
            onChange={(padding) => void writeEntry(combo.key, padding)}
          />
        );
      })}
    </div>
  );
}

function ComboPreview({
  combo,
  entry,
  inherited,
  unit,
  canEdit,
  fragments,
  width,
  onChange,
}: {
  combo: LayoutCombo;
  entry: PaddingSource | null;
  inherited: DocumentPadding;
  unit: MarginUnit;
  canEdit: boolean;
  fragments: VariantFragments[];
  width: number;
  onChange: (padding: PaddingSource | null) => void;
}) {
  const values = useMemo(
    () => effectiveValues(entry, inherited),
    [entry, inherited],
  );
  const [dragValues, setDragValues] = useState<DiagramValues | null>(null);
  const customised = isCustomised(entry);
  const label =
    combo.pageClass === "first"
      ? "Page 1"
      : combo.pageClass === "other"
        ? "Other pages"
        : "Even pages";
  return (
    <div className="flex flex-col items-center gap-1.5">
      <LetterheadSamplePreview
        fragments={fragments}
        padding={diagramValuesToPadding(dragValues ?? values)}
        pageIndex={PAGE_FOR_CLASS[combo.pageClass]}
        width={width}
        label={label}
        editableMargins
        unit={unit}
        marginValues={dragValues ?? values}
        disabled={!canEdit}
        onMarginChange={(key, px) =>
          setDragValues((prev) => ({ ...(prev ?? values), [key]: px }))
        }
        onMarginCommit={(key, px) => {
          setDragValues(null);
          onChange({ ...(entry ?? {}), [key]: px });
        }}
      />
      <div className="flex items-center gap-2 text-[10px] text-primary/40">
        <span
          className={cn(
            "rounded px-1.5 py-0.5",
            customised
              ? "bg-primary/10 text-primary"
              : "bg-secondary text-primary/50",
          )}
        >
          {customised ? "Custom margins" : "Firm default margins"}
        </span>
        {customised && canEdit && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-secondary"
          >
            <RotateCcw className="size-3" /> Reset
          </button>
        )}
      </div>
      {canEdit && (
        <p className="max-w-xs text-center text-[10px] text-primary/35">
          Drag any dashed edge to set that margin for this layout.
        </p>
      )}
    </div>
  );
}

export function LayoutMarginsEditor({
  combos,
  entries,
  inherited,
  unit,
  canEdit,
  onSave,
  fragments,
}: LayoutMarginsEditorProps) {
  const unusedEntries = useMemo(
    () =>
      entries.filter((e) => !combos.some((c) => layoutKeysEqual(c.key, e.key))),
    [entries, combos],
  );

  const writeEntry = async (
    key: LayoutCombo["key"],
    padding: PaddingSource | null,
  ) => {
    const rest = entries.filter((e) => !layoutKeysEqual(e.key, key));
    const next =
      padding && isCustomised(padding) ? [...rest, { key, padding }] : rest;
    try {
      await onSave(next);
    } catch {
      toast.error("Failed to save margins.");
    }
  };

  return (
    <div
      className="mt-4 border-t border-border pt-4"
      data-testid="layout-margins-editor"
    >
      <div className="mb-1 flex items-center text-[10px] font-medium uppercase tracking-wide text-primary/40">
        Margins per layout
      </div>
      <p className="mb-3 text-[11px] text-primary/45">
        Each combination of header, footer and sides this variant shows gets its
        own margins, and every precedent using this variant inherits them. Click
        Customise on a row to set that layout's margins; rows on firm defaults
        follow the Document Margins settings.
      </p>
      <div className="flex flex-col gap-2">
        {combos.map((combo) => {
          const entry =
            entries.find((e) => layoutKeysEqual(e.key, combo.key))?.padding ??
            null;
          return (
            <ComboRow
              key={combo.title}
              title={combo.title}
              entry={entry}
              inherited={inheritedFor(inherited, combo)}
              unit={unit}
              canEdit={canEdit}
              fragments={fragments}
              pageIndex={PAGE_FOR_CLASS[combo.pageClass]}
              onChange={(padding) => void writeEntry(combo.key, padding)}
            />
          );
        })}
        {unusedEntries.length > 0 && (
          <div className="mt-2">
            <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-primary/35">
              Unused
            </div>
            {unusedEntries.map((e, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded border border-dashed border-border/60 px-2 py-1 text-[11px] text-primary/50"
              >
                <span>
                  {describeLayoutKey(e.key)} (no longer produced by this
                  variant)
                </span>
                {canEdit && (
                  <button
                    type="button"
                    className="flex items-center gap-1 text-primary/50 hover:text-destructive"
                    onClick={() => void writeEntry(e.key, null)}
                  >
                    <Trash2 className="size-3" /> Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ComboRow({
  title,
  entry,
  inherited,
  unit,
  canEdit,
  onChange,
  fragments,
  pageIndex,
}: {
  title: string;
  entry: PaddingSource | null;
  inherited: DocumentPadding;
  unit: MarginUnit;
  canEdit: boolean;
  onChange: (padding: PaddingSource | null) => void;
  fragments?: VariantFragments[];
  pageIndex: number;
}) {
  const customised = isCustomised(entry);
  const [open, setOpen] = useState(customised);
  // Page-fit artwork ignores its zone's margins — lock those controls.
  const lockedZones = useMemo(
    () => zoneLocksForPage(fragments ?? [], pageIndex),
    [fragments, pageIndex],
  );
  const values = useMemo(
    () => effectiveValues(entry, inherited),
    [entry, inherited],
  );

  // Per-field string drafts so the input can be blank mid-edit.
  const [drafts, setDrafts] = useState<Record<FieldKey, string>>(() =>
    seedDrafts(entry, unit),
  );
  useEffect(() => {
    setDrafts(seedDrafts(entry, unit));
  }, [entry, unit]);

  // Live diagram values while dragging.
  const [dragValues, setDragValues] = useState<DiagramValues | null>(null);

  const commitField = (key: FieldKey) => {
    const raw = drafts[key] ?? "";
    const result = parseInForUnit(raw, unit);
    if (result.kind === "empty") {
      const next = { ...(entry ?? {}) };
      delete next[key];
      onChange(next);
      return;
    }
    if (result.kind !== "ok") {
      toast.error(result.reason);
      const currentValue = entry?.[key];
      setDrafts((d) => ({
        ...d,
        [key]: currentValue != null ? formatForUnit(currentValue, unit) : "",
      }));
      return;
    }
    if (entry?.[key] === result.px) return;
    onChange({ ...(entry ?? {}), [key]: result.px });
  };

  return (
    <div
      className={cn(
        "rounded-lg border border-border/60 bg-background/60",
        customised && "border-primary/30",
      )}
      data-testid="layout-combo-row"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="flex items-center gap-2 text-xs font-medium text-primary/80">
          {open ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
          {title}
        </span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px]",
              customised
                ? "bg-primary/10 text-primary"
                : "bg-secondary text-primary/50",
            )}
          >
            {customised ? "Customised" : "Firm defaults"}
          </span>
          {canEdit && (
            <span className="rounded border border-border/60 px-2 py-0.5 text-[11px] font-medium text-primary/70">
              {open ? "Done" : customised ? "Edit" : "Customise"}
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="border-t border-border/50 px-3 py-3">
          <div className="grid grid-cols-3 gap-3">
            {ZONES.map((zone) => {
              const locked =
                (zone.zone === "Header" && lockedZones.header) ||
                (zone.zone === "Footer" && lockedZones.footer);
              return (
                <div
                  key={zone.zone}
                  className="flex flex-col gap-1.5"
                  title={locked ? PAGE_FIT_LOCK_NOTE : undefined}
                >
                  <span className="text-[10px] font-medium uppercase tracking-wider text-primary/40">
                    {zone.zone}
                    {locked ? " · set by letterhead" : ""}
                  </span>
                  {zone.fields.map((field) => (
                    <div key={field.key} className="flex items-center gap-1.5">
                      <label className="w-10 text-[11px] text-primary/50">
                        {field.label}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`${title} ${zone.zone} ${field.label}`}
                        value={drafts[field.key] ?? ""}
                        placeholder={formatForUnit(
                          inherited[field.resolved],
                          unit,
                        )}
                        readOnly={!canEdit}
                        disabled={locked}
                        onChange={(e) =>
                          setDrafts((d) => ({
                            ...d,
                            [field.key]: e.target.value,
                          }))
                        }
                        onBlur={() =>
                          canEdit && !locked && commitField(field.key)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        className="h-6 w-14 rounded border border-border/50 bg-background px-1 text-center text-[11px] tabular-nums focus:border-primary/40 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                      />
                      <span className="text-[10px] text-primary/30">
                        {unit}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex flex-col items-center gap-1">
            {fragments && fragments.length > 0 ? (
              <>
                <LetterheadSamplePreview
                  fragments={fragments}
                  padding={diagramValuesToPadding(dragValues ?? values)}
                  pageIndex={pageIndex}
                  width={340}
                  editableMargins
                  unit={unit}
                  marginValues={dragValues ?? values}
                  disabled={!canEdit}
                  onMarginChange={(key, px) =>
                    setDragValues((prev) => ({
                      ...(prev ?? values),
                      [key]: px,
                    }))
                  }
                  onMarginCommit={(key, px) => {
                    setDragValues(null);
                    onChange({ ...(entry ?? {}), [key]: px });
                  }}
                />
                {!canEdit ? null : (
                  <p className="max-w-md text-center text-[10px] text-primary/35">
                    Drag any dashed edge on the preview to adjust that margin;
                    values save when you let go.
                  </p>
                )}
              </>
            ) : (
              <MarginsRulerDiagram
                values={dragValues ?? values}
                unit={unit}
                width={300}
                disabled={!canEdit}
                lockedZones={lockedZones}
                onChange={(key, px) =>
                  setDragValues((prev) => ({ ...(prev ?? values), [key]: px }))
                }
                onCommit={(key, px) => {
                  setDragValues(null);
                  onChange({ ...(entry ?? {}), [key]: px });
                }}
              />
            )}
          </div>

          {customised && canEdit && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => onChange(null)}
                className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-primary/50 hover:bg-secondary"
              >
                <RotateCcw className="size-3" /> Reset to firm defaults
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function seedDrafts(
  entry: PaddingSource | null,
  unit: MarginUnit,
): Record<FieldKey, string> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const f of ALL_FIELDS) {
    const v = entry?.[f.key];
    out[f.key] = v == null ? "" : formatForUnit(v, unit);
  }
  return out as Record<FieldKey, string>;
}
